import {
  ChoiceGroup,
  DetailsList,
  IconButton,
  Link,
  Panel,
  PanelType,
  SearchBox,
  SelectionMode,
  Stack,
  Text,
} from '@fluentui/react';
import type { IChoiceGroupOption } from '@fluentui/react';
import { useCallback, useState } from 'react';
import DataGridUI from '../DataGrid/DataGridUI';
import type { DataGridColumn } from '../DataGrid/DataGridUI';
import { useSearchInput } from '../hooks/useSearchInput';

/** Minimal user shape consumed by the picker. */
export interface IUserLike {
  id: number;
  username: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

/** Minimal group shape consumed by the picker. */
export interface IGroupLike {
  id: number;
  name: string;
}

export interface IPermissionItem {
  id: number;
  isGroup: boolean;
  username?: string;
  name?: string;
}

/**
 * The picker only needs the `items` array — accepting `IPagedCollection`
 * structurally lets portal services pass through unchanged while keeping
 * the surface minimal for new consumers.
 */
export type GetUsersFn = (
  abortController: AbortController,
  pageSize: number,
  pageNo: number,
  orderBy: string | undefined,
  search: string | undefined
) => Promise<{ items: IUserLike[] }>;

export type GetGroupsFn = (
  abortController: AbortController,
  pageSize: number,
  pageNo: number,
  orderBy: string | undefined,
  search: string | undefined
) => Promise<{ items: IGroupLike[] }>;

export interface IUserOrGroupSelectorProps {
  isOpen: boolean;
  hideUsersAndGroupsSelector: () => void;
  /**
   * When true, the picker uses the partner-prefix user-selector endpoint
   * via `getUsersRefValues` instead of the admin `getUsers` endpoint.
   */
  fromRefUserValues?: boolean;
  onUserOrGroupSelected: (
    id: number,
    isGroup: boolean,
    username?: string,
    name?: string,
    /**
     * Full row data for the selected user/group. Optional 5th arg —
     * existing portal callsites that only accept the first four are
     * unaffected.
     */
    item?: IUserLike
  ) => void;
  /** Restrict the picker to a single kind. */
  userOrGroupOnly?: 'user' | 'group';
  permissions?: IPermissionItem[];
  onRemovePermission?: (id: number) => void;
  /**
   * Service injection — supplied by the host app so this component can
   * live in `@moko/ui` without depending on portal/client services.
   *
   * Either pass `getUsers` (admin endpoint, full IUser shape) or
   * `getUsersRefValues` (partner endpoint, ref-value shape) — at least
   * one must be supplied for user search to work.
   */
  getUsers?: GetUsersFn;
  getUsersRefValues?: GetUsersFn;
  getGroups?: GetGroupsFn;
  /**
   * Minimum characters before an auto-search fires (default: 1). Empty is
   * always valid (clears results). Below the threshold, typing/Enter is a
   * no-op. Set to 3 to require at least 3 characters.
   */
  searchThreshold?: number;
}

const UserOrGroupSelector = (props: IUserOrGroupSelectorProps) => {
  const [usersOrGroups, setUsersOrGroups] = useState<IUserLike[]>([]);
  const [loading, setLoading] = useState(false);
  const [userOrGroup, setUserOrGroup] = useState<string | undefined>(
    props.userOrGroupOnly === 'group'
      ? 'group'
      : props.userOrGroupOnly === 'user'
      ? 'user'
      : undefined
  );

  const existingKeys = new Set(
    props.permissions?.map((p) => `${p.isGroup ? 'group' : 'user'}_${p.id}`) ??
      []
  );

  const runSearch = useCallback(
    (term: string) => {
      const abortController = new AbortController();
      setLoading(true);
      if (userOrGroup === 'user') {
        const fetcher = props.fromRefUserValues
          ? props.getUsersRefValues
          : props.getUsers;
        if (!fetcher) {
          setLoading(false);
          return;
        }
        fetcher(abortController, 10, 1, undefined, term)
          .then((users) => {
            setUsersOrGroups(
              users.items.map((u) => ({
                id: u.id,
                username: u.username,
                email: u.email,
                firstName: u.firstName,
                lastName: u.lastName,
              }))
            );
          })
          .finally(() => setLoading(false));
      } else {
        if (!props.getGroups) {
          setLoading(false);
          return;
        }
        props
          .getGroups(abortController, 10, 1, undefined, term)
          .then((groups) => {
            setUsersOrGroups(
              groups.items?.map((group) => ({
                id: group.id,
                username: group.name,
                email: '',
              }))
            );
          })
          .finally(() => setLoading(false));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      userOrGroup,
      props.fromRefUserValues,
      props.getUsers,
      props.getUsersRefValues,
      props.getGroups,
    ]
  );

  const {
    getInputProps,
    value: search,
    reset,
  } = useSearchInput({
    onSearch: runSearch,
    threshold: props.searchThreshold,
  });
  const inputProps = getInputProps();

  const columns: DataGridColumn<IUserLike>[] = [
    {
      key: 'id',
      ellipsis: true,
      name: 'Id',
      minWidth: 50,
      renderCell: (item) => {
        const key = `${userOrGroup === 'group' ? 'group' : 'user'}_${item.id}`;
        const alreadyAdded = existingKeys.has(key);
        return alreadyAdded ? (
          <Text style={{ color: 'var(--colorNeutralForeground4)' }}>
            {item.id}
          </Text>
        ) : (
          <Link
            key={item.id}
            onClick={() => {
              props.onUserOrGroupSelected(
                item.id,
                userOrGroup === 'group',
                item.username,
                (item as unknown as { name?: string }).name,
                item
              );
            }}
          >
            {item.id}
          </Link>
        );
      },
    },
    {
      key: 'username',
      ellipsis: true,
      name:
        props.userOrGroupOnly === 'user'
          ? 'User name'
          : props.userOrGroupOnly === 'group'
          ? 'Group name'
          : 'Group/User name',
      minWidth: 300,
    },
    ...(props.userOrGroupOnly === 'group'
      ? []
      : [
          {
            key: 'email',
            name: 'Email',
            ellipsis: true,
          } as DataGridColumn<IUserLike>,
        ]),
  ];

  return (
    <Panel
      isLightDismiss
      isOpen={props.isOpen}
      onDismiss={props.hideUsersAndGroupsSelector}
      closeButtonAriaLabel="Close"
      type={PanelType.medium}
      headerText={
        props.userOrGroupOnly === 'user'
          ? 'Pick a user'
          : props.userOrGroupOnly === 'group'
          ? 'Pick a group'
          : 'Pick user or group to set permission'
      }
      styles={{ content: { paddingTop: 24 } }}
    >
      {props.permissions && (
        <>
          <Text variant="mediumPlus" style={{ fontWeight: 600 }}>
            Permission
          </Text>
          <DetailsList
            items={props.permissions}
            selectionMode={SelectionMode.none}
            columns={[
              {
                fieldName: 'name',
                key: 'Username',
                name: 'User|Group Name',
                minWidth: 180,
              },
              {
                fieldName: 'id',
                key: 'Id',
                name: 'User|Group Id',
                minWidth: 90,
                onRender(item: IPermissionItem) {
                  return (
                    <Stack horizontal tokens={{ childrenGap: 5 }}>
                      <Text variant="large">{item.id}</Text>
                      <Text variant="small">
                        {item.isGroup ? '(Group)' : '(User)'}
                      </Text>
                    </Stack>
                  );
                },
              },
              {
                fieldName: 'isGroup',
                key: 'IsGroup',
                name: 'IsGroup',
                minWidth: 50,
              },
              ...(props.onRemovePermission
                ? [
                    {
                      key: 'Action',
                      name: '',
                      minWidth: 50,
                      onRender: (item?: IPermissionItem) =>
                        item ? (
                          <IconButton
                            iconProps={{ iconName: 'Delete' }}
                            onClick={() => props.onRemovePermission?.(item.id)}
                          />
                        ) : null,
                    },
                  ]
                : []),
            ]}
          />
        </>
      )}
      {props.userOrGroupOnly ? (
        <></>
      ) : (
        <div style={{ paddingTop: 20 }}>
          <Text variant="mediumPlus" style={{ fontWeight: 600 }}>
            Pick one
          </Text>
          <ChoiceGroup
            defaultSelectedKey="B"
            options={[
              { key: 'user', text: 'User' },
              { key: 'group', text: 'Group' },
            ]}
            onChange={(
              _ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
              option?: IChoiceGroupOption
            ) => setUserOrGroup(option?.key ?? 'user')}
            required={true}
          />
        </div>
      )}
      <SearchBox
        placeholder={`Search ${userOrGroup === 'user' ? 'users' : 'groups'}`}
        value={search}
        onChange={(_ev, newValue) =>
          inputProps.onChange({
            target: { value: newValue ?? '' },
          } as React.ChangeEvent<HTMLInputElement>)
        }
        onSearch={(newValue: string) =>
          inputProps.onKeyDown({
            key: 'Enter',
            currentTarget: { value: newValue },
          } as unknown as React.KeyboardEvent<HTMLInputElement>)
        }
        onClear={() => {
          reset('');
          setUsersOrGroups([]);
        }}
      />
      {(loading || usersOrGroups.length > 0) && (
        <DataGridUI<IUserLike>
          items={usersOrGroups}
          columns={columns}
          loading={loading}
          getRowId={(item) => item.id}
        />
      )}
    </Panel>
  );
};

export default UserOrGroupSelector;
